import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { toaster } from '@kp/react-ui';

import { useQuery } from '@tanstack/react-query';

import { getTenantApplications } from '@/api/application';

import { APP_CLIENT_IDS } from '../constants';
import type { AppAccess } from '../types/home-types';

const LOAD_ERROR_TOAST = 'home.apps.loadError';

const TOAST_DISMISS_TIMEOUT = 8000;

const CLIENT_IDS: string[] = Object.values(APP_CLIENT_IDS);

/**
 * Which of the Home apps the current tenant can open. An app is enabled only
 * if the tenant can sign in to it and the backend tells its URL; otherwise its
 * card is disabled. A failed request disables every app.
 */
export const useAppAccess = () => {
  const { t } = useTranslation();

  const { data, error, isLoading } = useQuery({
    queryKey: ['getTenantApplications', CLIENT_IDS],
    queryFn: () => getTenantApplications(CLIENT_IDS),
  });

  useEffect(() => {
    if (error) {
      toaster.add({
        id: LOAD_ERROR_TOAST,
        content: t('home.apps.errors.load'),
        type: 'error',
        dismissTimeout: TOAST_DISMISS_TIMEOUT,
      });
    }
  }, [error, t]);

  const byClientId = new Map(
    (data?.data ?? []).map((app) => [app.clientId, app]),
  );

  /** @param clientId  One of `APP_CLIENT_IDS` */
  const getAccess = (clientId: string): AppAccess => {
    if (isLoading) {
      return { disabled: false, loading: true };
    }

    const app = byClientId.get(clientId);
    const url = app?.isAvailable ? (app.url ?? undefined) : undefined;
    return url
      ? { url, disabled: false, loading: false }
      : { disabled: true, loading: false };
  };

  return { getAccess };
};
